import React from 'react';
import PropTypes from 'prop-types';

import { formatPrice } from '../../services/formatPrice';

import getSubscriptionIntervalWithSlash from '../../services/translation/getSubscriptionIntervalWithSlash';
import MultilineText from '../../components/MultilineText';
import clsx from 'clsx';
import Badge from '../../components/Badge';
import ModulesEnum, {
  getEventModules,
  getFormModules,
  getProductModules,
} from '../../enums/modules';
import sanitizeHtml from 'sanitize-html';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import checkHasValidStartDate from '../../lib/checkHasValidStartDate';
import { getImageSrcAndSrcSet, stripQueryStringFromCdnUrl } from '../../services/getUrlFromCdn';
import useCartCost from '../../hooks/useCartCost';
import useCheckoutState, { checkoutLayouts } from '../../hooks/useCheckoutState';
import * as Avatar from '@radix-ui/react-avatar';
import getSafeReadableColor from '../../lib/getSafeReadableColor';
import { PAGE_TYPES } from '../../enums/page-types';
import { getMedia } from '../../lib/getMedia';
import getPlanIterations from '../../lib/getPlanIterations';
import getColors from '../../lib/getColors';
import { splitBulkDiscounts } from '../../lib/splitBulkDiscounts';
import getBulkDiscount from '../../lib/getBulkDiscount';
import useQuantity from '../../hooks/useQuantity';
import useProration from '../../hooks/useProration';
import { getDescription } from '../../lib/getDescription';

function getTitle({ allowDynamicTitle, dynamicTitle, title }) {
  if (!allowDynamicTitle) return title;

  if (dynamicTitle) {
    return sanitizeHtml(dynamicTitle.replace(/\\n/g, '\n'));
  }

  return title;
}

const propTypes = {
  checkout: PropTypes.object.isRequired,
  seller: PropTypes.object.isRequired,
};

const Product = () => {
  const { isScreenshot, checkoutLayout, checkout, seller, query, isPreview } = useCheckoutState();
  const { productPriceAmount, setupFeeAmount } = useCartCost();
  const { secondaryTextColor } = getColors(checkout, checkoutLayout);

  const { product } = checkout;

  let modules;
  if (checkout.type === PAGE_TYPES.CHECKOUT) {
    modules = getProductModules({
      checkout,
      seller,
      query,
      isScreenshot,
      productPriceAmount,
      setupFeeAmount,
      discountedFromPrice: product.discountedFromPrice,
      trialPeriodDays: product.trialPeriodDays,
      startDate: product.startDate,
      isPreview,
    });
  } else if (checkout.type === PAGE_TYPES.FORM) {
    modules = getFormModules({
      checkout,
      seller,
      isScreenshot,
      query,
      isPreview,
    });
  } else if (checkout.type === PAGE_TYPES.EVENT) {
    modules = getEventModules({
      page: checkout,
      seller,
      isScreenshot,
      query,
      isPreview,
    });
  }

  const siteUrl = seller.url;

  const ImageWrapper = siteUrl ? 'a' : 'div';

  const isSoldOut = product && !product.hasUnlimitedStock && product.stock === 0;

  const title = getTitle({
    allowDynamicTitle: checkout.allowDynamicTitle,
    title: product?.title || checkout.title,
    dynamicTitle: query.title,
  });

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      productDescription: product?.description,
      productDescriptionHtml: product?.descriptionHtml,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  const { primaryTextColor, accentColor } = getColors(checkout, checkoutLayout);

  const media = getMedia({ checkout, product });
  const hasMedia = media && media.length > 0;

  return (
    <div>
      <div
        className={clsx('cp_product-wrapper flex flex-col w-full gap-y-2', {
          'mt-10':
            modules[ModulesEnum.LOGO] && !hasMedia && checkoutLayout === checkoutLayouts.CENTER,
          'pt-4': checkoutLayout === checkoutLayouts.CENTER && !hasMedia,
          'mt-10 md:mt-0':
            modules[ModulesEnum.LOGO] && !hasMedia && checkoutLayout === checkoutLayouts.SPLIT,
          'pt-4 md:pt-0': checkoutLayout === checkoutLayouts.SPLIT && !hasMedia,
        })}
        style={{
          color: primaryTextColor,
        }}
      >
        {modules[ModulesEnum.LOGO] &&
          (checkoutLayout === checkoutLayouts.CENTER ||
            checkoutLayout === checkoutLayouts.SPLIT) && (
            <div
              className={clsx('cp_product-logo-wrapper px-4 relative pt-4', {
                'md:hidden': checkoutLayout === checkoutLayouts.SPLIT,
              })}
            >
              <ImageWrapper
                className={clsx(
                  'cp_product-logo block rounded-full w-20 h-20 overflow-hidden -top-14 left-0 right-0 mx-auto absolute shadow-sm text-5xl',
                  {
                    'hover:shadow': !!siteUrl,
                  },
                )}
                rel="nofollow noreferrer"
                href={siteUrl}
                target="_blank"
              >
                {/* This is double the size of w-20 */}
                <Logo width="80" height="80" />
              </ImageWrapper>
            </div>
          )}

        {(modules[ModulesEnum.TITLE] ||
          modules[ModulesEnum.PRICE] ||
          modules[ModulesEnum.SETUP_FEE] ||
          modules[ModulesEnum.BUSINESS_NAME]) && (
          <div
            className={clsx(`cp_product-content`, {
              'text-center': checkout.textAlign === 'center',
            })}
          >
            <div className="cp_product-title-wrapper justify-between items-start space-y-1">
              {modules[ModulesEnum.TITLE] && (
                <div className="cp_product-title-container">
                  <h1 className="cp_product-title text-2xl font-bold leading-tight break-words">
                    {title}
                  </h1>
                </div>
              )}

              {modules[ModulesEnum.PRICE] && (
                <Price product={product} amount={productPriceAmount} checkout={checkout} />
              )}

              {modules[ModulesEnum.SETUP_FEE] && (
                <SetupFee
                  product={product}
                  checkout={checkout}
                  secondaryTextColor={secondaryTextColor}
                />
              )}
            </div>

            {isSoldOut ? (
              <Badge className="cp_sold-out cp_product-sold-out text-sm bg-gray-900 text-white">
                {checkout.soldOutText}
              </Badge>
            ) : (
              ''
            )}

            {modules[ModulesEnum.BUSINESS_NAME] &&
              (checkoutLayout === checkoutLayouts.CENTER ||
                checkoutLayout === checkoutLayouts.SPLIT) && (
                <div
                  className={clsx({
                    'md:hidden': checkoutLayout === checkoutLayouts.SPLIT,
                  })}
                >
                  <MerchantName />
                </div>
              )}
          </div>
        )}

        {modules[ModulesEnum.DESCRIPTION] && (
          <div
            className={clsx('overflow-hidden cp_product-description-wrapper', {
              'text-center': checkout.textAlign === 'center',
              ...(checkoutLayout === checkoutLayouts.DOUBLE && {
                color: primaryTextColor,
              }),
            })}
          >
            <style
              dangerouslySetInnerHTML={{
                __html: `
                    body {
                      --store-code-accent-color: ${accentColor};
                    }
                `,
              }}
            />

            <MultilineText
              className="cp_product-description"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

Product.propTypes = propTypes;

export default Product;

export function StoreNameWithLabel() {
  const { seller, checkout, checkoutLayout } = useCheckoutState();

  const { primaryTextColor } = getColors(checkout, checkoutLayout);

  const siteUrl = seller.url;

  if (!checkout.showStoreNameWithLabel) {
    return null;
  }

  return (
    <div className="cp_sold-by-wrapper mb-4">
      {siteUrl ? (
        <a
          href={siteUrl}
          target="_blank"
          rel="nofollow noreferrer"
          className={clsx('flex gap-x-2 items-center mt-px', {
            'justify-center': checkout.logoAlign === 'center',
          })}
        >
          <div className="w-7 h-7 rounded-full overflow-hidden">
            <Logo width="28" height="28" />
          </div>

          <div
            style={{
              color: primaryTextColor,
            }}
            className="font-medium cp_sold-by-seller"
          >
            {seller.displayName}
          </div>
        </a>
      ) : (
        <div
          className={clsx('flex gap-x-2 items-center mb-6 mt-px', {
            'mb-6':
              checkoutLayout === checkoutLayouts.DOUBLE || checkoutLayout === checkoutLayouts.SPLIT,
            'justify-center': checkout.logoAlign === 'center',
          })}
        >
          <div className="cp_sold-by-logo w-7 h-7 rounded-full overflow-hidden">
            <Logo width="28" height="28" />
          </div>

          <div
            style={{
              color: primaryTextColor,
            }}
            className="cp_sold-by-seller font-medium"
          >
            {seller.displayName}
          </div>
        </div>
      )}
    </div>
  );
}

function MerchantName() {
  const { seller } = useCheckoutState();
  const siteUrl = seller.url;

  return (
    <div className="cp_sold-by-wrapper mt-1">
      {siteUrl ? (
        <a
          href={siteUrl}
          className="cp_sold-by-seller font-medium underline"
          target="_blank"
          rel="nofollow noreferrer"
        >
          {seller.displayName}
        </a>
      ) : (
        <span className="cp_sold-by-seller font-medium">{seller.displayName}</span>
      )}
    </div>
  );
}

export function PriceWrapper({ children }) {
  return <div className="cp_product-price-wrapper inline-flex gap-x-2">{children}</div>;
}

export function SetupFee({ product, checkout, secondaryTextColor }) {
  if (!product.setupFee) return '';

  return (
    <div className="cp_setup-fee text-base" style={{ color: secondaryTextColor }}>
      <span className="cp_setup-fee-amount">
        {formatPrice({
          amount: product.setupFee,
          currency: product.currency,
          locale: product.locale,
          style: 'currency',
        })}{' '}
      </span>
      <span className="cp_setup-fee-text">{checkout.setupFeeText}</span>
    </div>
  );
}

export function DiscountedFromPrice({ product, checkout, priceBeforeBulkDiscount }) {
  const { query } = useCheckoutState();

  const dynamicDiscountedFromPrice = Number(query?.discounted_from);

  let discountedFromPrice =
    checkout.allowDynamicDiscountedFromPrice && dynamicDiscountedFromPrice >= 0
      ? dynamicDiscountedFromPrice
      : product.discountedFromPrice;

  // Use the priceBeforeBulkDiscount if provided and there's no existing discounted from price
  if (!discountedFromPrice && priceBeforeBulkDiscount) {
    discountedFromPrice = priceBeforeBulkDiscount;
  }

  if (!discountedFromPrice) return '';

  if (product.payWhatYouWant) return '';

  return (
    <span className="cp_product-discounted-from-price line-through mr-2">
      {formatPrice({
        amount: discountedFromPrice,
        currency: product.currency,
        locale: checkout.locale,
        style: 'currency',
      })}
    </span>
  );
}

export function Price({ product, amount, checkout }) {
  const { checkoutLayout, dynamicPlanIterations, query } = useCheckoutState();
  const { secondaryTextColor } = getColors(checkout, checkoutLayout);
  const quantity = useQuantity();
  const { proration, hasProration, isLoadingProration } = useProration();
  // Split bulk discounts and get only the subtotal discounts
  const { subtotalDiscounts } = splitBulkDiscounts(product.discounts);

  // Calculate bulk discount
  const { bulkDiscountAmount } = getBulkDiscount(subtotalDiscounts, quantity, amount * quantity);

  const discountedPrice = amount - bulkDiscountAmount / quantity;

  const planIterations = getPlanIterations({
    productPlanIterations: product.planIterations,
    allowDynamicPlanIterations: checkout.allowDynamicPlanIterations,
    dynamicPlanIterations,
  });

  const dynamicDiscountedFromPrice = Number(query?.discounted_from);

  let discountedFromPrice =
    checkout.allowDynamicDiscountedFromPrice && dynamicDiscountedFromPrice >= 0
      ? dynamicDiscountedFromPrice
      : product.discountedFromPrice;

  // if bulk discount makes the price free, we still want to show the price
  // if the price was 0 to begin with, we don't want to show it

  // one-time charges
  if (product.type === 'charge') {
    const shouldShowPrice =
      amount > 0 || (amount === 0 && (discountedPrice > 0 || discountedFromPrice > 0));

    if (!shouldShowPrice) return '';

    return (
      <PriceWrapper>
        <div className="cp_product-price text-2xl ">
          <DiscountedFromPrice
            product={product}
            checkout={checkout}
            priceBeforeBulkDiscount={bulkDiscountAmount > 0 ? amount : null}
          />

          {shouldShowPrice && (
            <>
              <span className="cp_product-price-amount">
                {formatPrice({
                  amount: discountedPrice,
                  currency: product.currency,
                  locale: checkout.locale,
                  style: 'currency',
                })}
              </span>

              <span className="cp_product-price-plus-sign">
                {product.payWhatYouWant ? '+' : ''}
              </span>
            </>
          )}
        </div>
      </PriceWrapper>
    );
  }

  // subscriptions with trial period
  if (product.trialPeriodDays) {
    return (
      <PriceWrapper>
        <div className="cp_product-price text-2xl">
          <span className="cp_product-price-trial-period">
            <span className="cp_product-price-trial-period-days-text">
              {product.trialPeriodDays}{' '}
            </span>
            <span className="cp_product-price-trial-period-days-free-text">
              {checkout.trialDaysFreeText}{' '}
            </span>
          </span>

          {(amount > 0 || discountedFromPrice > 0) && (
            <>
              <div
                className="cp_product-price-trial-then-price text-base"
                style={{ color: secondaryTextColor }}
              >
                <span className="cp_product-price-trial-then-text">{checkout.trialThenText} </span>
                <DiscountedFromPrice product={product} checkout={checkout} />
                <span className="cp_product-price-trial-then-amount">
                  {formatPrice({
                    amount,
                    currency: product.currency,
                    locale: checkout.locale,
                    style: 'currency',
                  })}{' '}
                </span>
                <span className="cp_product-price-trial-then-interval">
                  {getSubscriptionIntervalWithSlash(
                    product.interval,
                    product.intervalCount,
                    checkout,
                  )}
                </span>
              </div>
            </>
          )}

          {checkHasValidStartDate(product.startDate) ? (
            <div
              className="cp_product-price-starts-on-text text-base"
              style={{ color: secondaryTextColor }}
            >
              <span className="cp_product-price-starts-on-text">{checkout.startsOnText} </span>
              <span className="cp_product-price-starts-on-date">
                {format(new Date(product.startDate), 'd MMM, yyyy')}
              </span>
            </div>
          ) : (
            ''
          )}

          {planIterations ? (
            <div
              className="cp_product-price-ends-after-text text-base"
              style={{ color: secondaryTextColor }}
            >
              <span className="cp_product-price-ends-after-text">{checkout.endsAfterText} </span>
              <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
              <span className="cp_product-price-ends-after-payments-text">
                {checkout.endsAfterPaymentsText}
              </span>
            </div>
          ) : (
            ''
          )}

          {hasProration && !isLoadingProration && (
            <div
              className="cp_product-price-proration-wrapper text-base"
              style={{ color: secondaryTextColor }}
            >
              <div className="cp_product-price-proration-date-wrapper">
                <span className="cp_product-price-proration-date-text">
                  {checkout.renewsOnText}{' '}
                </span>
                {proration.nextBillingDate && (
                  <span className="cp_product-price-proration-date">
                    {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                  </span>
                )}
              </div>

              <div className="cp_product-price-proration-amount-wrapper">
                <span className="cp_product-price-proration-amount-today-text">
                  {checkout.todayText}{' '}
                </span>
                <span className="cp_product-price-proration-amount">
                  {formatPrice({
                    amount: proration.amount,
                    currency: product.currency,
                    locale: checkout.locale,
                    style: 'currency',
                  })}{' '}
                </span>
                <span className="cp_product-price-proration-amount-text">
                  {checkout.proratedAmountText}
                </span>
              </div>
            </div>
          )}
        </div>
      </PriceWrapper>
    );
  }

  // subscriptions with start date
  if (checkHasValidStartDate(product.startDate)) {
    return (
      <PriceWrapper>
        <div className="cp_product-price text-2xl">
          <DiscountedFromPrice product={product} checkout={checkout} />

          {(amount > 0 || discountedFromPrice > 0) && (
            <>
              <span className="cp_product-price-amount">
                {formatPrice({
                  amount,
                  currency: product.currency,
                  locale: checkout.locale,
                  style: 'currency',
                })}{' '}
              </span>
              <span className="cp_product-price-interval">
                {getSubscriptionIntervalWithSlash(
                  product.interval,
                  product.intervalCount,
                  checkout,
                )}
              </span>
            </>
          )}

          <div
            className="cp_product-price-starts-on-wrapper text-base"
            style={{ color: secondaryTextColor }}
          >
            <span className="cp_product-price-starts-on-text">{checkout.startsOnText} </span>
            <span className="cp_product-price-starts-on-date">
              {format(new Date(product.startDate), 'd MMM, yyyy')}
            </span>
          </div>

          {amount > 0 && planIterations ? (
            <div
              className="cp_product-price-ends-after-wrapper text-base"
              style={{ color: secondaryTextColor }}
            >
              <span className="cp_product-price-ends-after-text">{checkout.endsAfterText} </span>
              <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
              <span className="cp_product-price-ends-after-payments-text">
                {checkout.endsAfterPaymentsText}
              </span>
            </div>
          ) : (
            ''
          )}

          {hasProration && !isLoadingProration && (
            <div
              className="cp_product-price-proration-wrapper text-base"
              style={{ color: secondaryTextColor }}
            >
              <div className="cp_product-price-proration-date-wrapper">
                <span className="cp_product-price-proration-date-text">
                  {checkout.renewsOnText}{' '}
                </span>
                {proration.nextBillingDate && (
                  <span className="cp_product-price-proration-date">
                    {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                  </span>
                )}
              </div>

              <div className="cp_product-price-proration-amount-wrapper">
                <span className="cp_product-price-proration-amount-today-text">
                  {checkout.todayText}{' '}
                </span>
                <span className="cp_product-price-proration-amount">
                  {formatPrice({
                    amount: proration.amount,
                    currency: product.currency,
                    locale: checkout.locale,
                    style: 'currency',
                  })}{' '}
                </span>
                <span className="cp_product-price-proration-amount-text">
                  {checkout.proratedAmountText}
                </span>
              </div>
            </div>
          )}
        </div>
      </PriceWrapper>
    );
  }

  const shouldShowPrice = amount > 0 || discountedFromPrice > 0;

  if (!shouldShowPrice) return '';

  return (
    <PriceWrapper>
      <div className="cp_product-price text-2xl">
        <DiscountedFromPrice product={product} checkout={checkout} />

        {(amount > 0 || discountedFromPrice > 0) && (
          <>
            <span className="cp_product-price-amount">
              {formatPrice({
                amount,
                currency: product.currency,
                locale: checkout.locale,
                style: 'currency',
              })}{' '}
            </span>
            <span className="cp_product-price-interval">
              {getSubscriptionIntervalWithSlash(product.interval, product.intervalCount, checkout)}
            </span>
          </>
        )}

        {checkHasValidStartDate(product.startDate) ? (
          <div
            className="cp_product-price-starts-on-wrapper text-base"
            style={{ color: secondaryTextColor }}
          >
            <span className="cp_product-price-starts-on-text">{checkout.startsOnText} </span>
            <span className="cp_product-price-starts-on-date">
              {format(new Date(product.startDate), 'd MMM, yyyy')}
            </span>
          </div>
        ) : (
          ''
        )}

        {amount > 0 && planIterations ? (
          <div
            className="cp_product-price-ends-after-wrapper text-base"
            style={{ color: secondaryTextColor }}
          >
            <span className="cp_product-price-ends-after-text">{checkout.endsAfterText} </span>
            <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
            <span className="cp_product-price-ends-after-payments-text">
              {checkout.endsAfterPaymentsText}
            </span>
          </div>
        ) : (
          ''
        )}

        {hasProration && !isLoadingProration && (
          <div
            className="cp_product-price-proration-wrapper text-base"
            style={{ color: secondaryTextColor }}
          >
            <div className="cp_product-price-proration-date-wrapper">
              <span className="cp_product-price-proration-date-text">{checkout.renewsOnText} </span>
              {proration.nextBillingDate && (
                <span className="cp_product-price-proration-date">
                  {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                </span>
              )}

              <div className="cp_product-price-proration-amount-wrapper">
                <span className="cp_product-price-proration-amount-today-text">
                  {checkout.todayText}{' '}
                </span>
                <span className="cp_product-price-proration-amount">
                  {formatPrice({
                    amount: proration.amount,
                    currency: product.currency,
                    locale: checkout.locale,
                    style: 'currency',
                  })}{' '}
                </span>
                <span className="cp_product-price-proration-amount-text">
                  {checkout.proratedAmountText}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </PriceWrapper>
  );
}

const Logo = React.memo(({ width, height }) => {
  const { seller, isPreview } = useCheckoutState();
  const logo = getImageSrcAndSrcSet({ url: seller.logo?.url, width, height, isPreview });

  if (!logo) {
    return (
      <Avatar.Root className="cp_product-logo-fallback-wrapper">
        <Avatar.Fallback
          className="cp_product-logo-fallback w-full h-full flex rounded-full uppercase items-center justify-center overflow-hidden font-bold leading-loose"
          style={{
            background: seller.brandColor,
            color: getSafeReadableColor(seller.brandColor),
          }}
        >
          {seller.displayName?.charAt(0)}
        </Avatar.Fallback>
      </Avatar.Root>
    );
  }

  return (
    <img
      src={logo.src}
      srcSet={logo.srcSet}
      alt={seller.name}
      className="cp_product-logo rounded-full"
      width={width}
      height={height}
      loading="eager"
    />
  );
});
